// Built-ins
import { useState, useEffect } from "react";

// Icons / Images
import { FiEdit } from "react-icons/fi";
import axios from "axios";

// Modules / Functions
import Swal from "sweetalert2";
import { Modal, Form } from "react-bootstrap";
import GuardedModal from "../common/GuardedModal";
import { Button } from "semantic-ui-react";

// Global Variables
import { headers } from "../global_vars";
import SelectWithInlineCreate from "../common/SelectWithInlineCreate";
import AutocompleteSelect from "../common/AutocompleteSelect";
import AddCountryModal from "./create/add_country";
import AddProvinceModal from "./create/add_province";
import AddCityModal from "./create/add_city";
import { cityOptionLabel } from "../common/cityOptionLabel";
import {
  formatDecimalWithComma,
  isValidDecimalInput,
  parseDecimalInput,
  sanitizeDecimalInput,
} from "../common/decimalFormat";
import RichTextEditor from "../common/RichTextEditor";
import { htmlForApi } from "../../utils/richText";

// Variables
window.Swal = Swal;

// API endpoints
const UPDATE_PROPERTY = "https://ultima.icsgr.com/api/data_management/property/";
const ASSOCIATED_CLIENTS = "https://ultima.icsgr.com/api/data_management/associated_clients/";

// Edit Property ID Modal
export function EditPropertyIdModal({ property, update_state }) {
  return null;
}

// Edit Property Description Modal
export function EditPropertyDescriptionModal({ property, update_state }) {
  const [show, setShow] = useState(false);
  const [description, setDescription] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (show) {
      setDescription(property.description || "");
    }
  }, [show, property]);

  const handleClose = () => setShow(false);
  const handleShow = () => setShow(true);

  const handleSave = async () => {
    if (!description.trim()) {
      Swal.fire("Error", "Description is required", "error");
      return;
    }

    setIsLoading(true);
    try {
      const currentHeaders = {
        ...headers,
        "Authorization": "Token " + localStorage.getItem("userToken")
      };

      const response = await axios.put(
        UPDATE_PROPERTY + property.property_id + "/",
        { description: description.trim() },
        { headers: currentHeaders }
      );

      Swal.fire("Success", "Description updated successfully", "success");
      if (update_state) update_state(response.data);
      handleClose();
    } catch (e) {
      console.error('Error updating description:', e);
      const apiMsg = e?.response?.data?.detail || e?.response?.data || "Failed to update description";
      Swal.fire("Error", apiMsg, "error");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <Button size="tiny" basic onClick={handleShow}><FiEdit style={{ marginRight: 6 }} /> Edit Description</Button>

      <GuardedModal show={show} onHide={handleClose} centered>
        <Modal.Header closeButton>
          <Modal.Title>Edit Description</Modal.Title>
        </Modal.Header>
        <Modal.Body>
            <Form.Group>
              <Form.Label>Description *:</Form.Label>
              <Form.Control
                type="text"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Enter description"
                maxLength={80}
              />
            </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <small className="mr-auto"><div style={{ color: description.trim() ? "green" : "red" }}>{description.trim() ? "Looks good." : "Description is required."}</div></small>
          <Button color="red" onClick={handleClose} disabled={isLoading}>Cancel</Button>
          <Button color="green" onClick={handleSave} disabled={!description.trim() || isLoading}>{isLoading ? "Saving..." : "Save Changes"}</Button>
        </Modal.Footer>
      </GuardedModal>
    </>
  );
}

// Edit Property Project Modal
export function EditPropertyProjectModal({ property, update_state }) {
  const [show, setShow] = useState(false);
  const [project, setProject] = useState("");
  const [projects, setProjects] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (show) {
      setProject(property.project?.project_id || "");
      loadProjects();
    }
  }, [show, property]);

  const loadProjects = async () => {
    try {
      const currentHeaders = {
        ...headers,
        "Authorization": "Token " + localStorage.getItem("userToken")
      };
      const response = await axios.get("https://ultima.icsgr.com/api/data_management/all_projects/", {
        headers: currentHeaders
      });
      const projectsData = response?.data?.all_projects || [];
      setProjects(projectsData);
    } catch (error) {
      console.error('Error loading projects:', error);
      setProjects([]);
    }
  };

  const handleClose = () => setShow(false);
  const handleShow = () => setShow(true);

  const handleSave = async () => {
    if (!project.trim()) {
      Swal.fire("Error", "Project is required", "error");
      return;
    }

    setIsLoading(true);
    try {
      const currentHeaders = {
        ...headers,
        "Authorization": "Token " + localStorage.getItem("userToken")
      };

      const response = await axios.put(
        UPDATE_PROPERTY + property.property_id + "/",
        { project_id: project },
        { headers: currentHeaders }
      );

      Swal.fire("Success", "Project updated successfully", "success");
      if (update_state) update_state(response.data);
      handleClose();
    } catch (e) {
      console.error('Error updating project:', e);
      const apiMsg = e?.response?.data?.detail || e?.response?.data || "Failed to update project";
      Swal.fire("Error", apiMsg, "error");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <Button size="tiny" basic onClick={handleShow}><FiEdit style={{ marginRight: 6 }} /> Edit Project</Button>

      <GuardedModal show={show} onHide={handleClose} centered>
        <Modal.Header closeButton>
          <Modal.Title>Edit Project</Modal.Title>
        </Modal.Header>
        <Modal.Body>
            <Form.Group>
              <Form.Label>Project *:</Form.Label>
              <AutocompleteSelect value={project} onChange={setProject} placeholder="Select Project">
                {projects.map((proj) => (
                  <option key={proj.project_id} value={proj.project_id}>
                    {proj.title}
                  </option>
                ))}
              </AutocompleteSelect>
            </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <small className="mr-auto"><div style={{ color: project.trim() ? "green" : "red" }}>{project.trim() ? "Looks good." : "Project is required."}</div></small>
          <Button color="red" onClick={handleClose} disabled={isLoading}>Cancel</Button>
          <Button color="green" onClick={handleSave} disabled={!project.trim() || isLoading}>{isLoading ? "Saving..." : "Save Changes"}</Button>
        </Modal.Footer>
      </GuardedModal>
    </>
  );
}

// Edit Property Country Modal
export function EditPropertyCountryModal({ property, update_state }) {
  const [show, setShow] = useState(false);
  const [country, setCountry] = useState("");
  const [countries, setCountries] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [addCountryOpen, setAddCountryOpen] = useState(false);

  useEffect(() => {
    if (show) {
      setCountry(property.country?.country_id || "");
      loadCountries();
    }
  }, [show, property]);

  const loadCountries = async () => {
    try {
      const currentHeaders = {
        ...headers,
        "Authorization": "Token " + localStorage.getItem("userToken")
      };
              const response = await axios.get("https://ultima.icsgr.com/api/regions/all_countries/", {
        headers: currentHeaders
      });
      const countriesData = response?.data?.all_countries || [];
      setCountries(countriesData);
    } catch (error) {
      console.error('Error loading countries:', error);
      setCountries([]);
    }
  };

  const handleClose = () => setShow(false);
  const handleShow = () => setShow(true);

  const handleSave = async () => {
    if (!country.trim()) {
      Swal.fire("Error", "Country is required", "error");
      return;
    }

    setIsLoading(true);
    try {
      const currentHeaders = {
        ...headers,
        "Authorization": "Token " + localStorage.getItem("userToken")
      };

      const response = await axios.put(
        UPDATE_PROPERTY + property.property_id + "/",
        { country_id: country },
        { headers: currentHeaders }
      );

      Swal.fire("Success", "Country updated successfully", "success");
      if (update_state) update_state(response.data);
      handleClose();
    } catch (e) {
      console.error('Error updating country:', e);
      const apiMsg = e?.response?.data?.detail || e?.response?.data || "Failed to update country";
      Swal.fire("Error", apiMsg, "error");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <Button size="tiny" basic onClick={handleShow}><FiEdit style={{ marginRight: 6 }} /> Edit Country</Button>

      <GuardedModal show={show} onHide={handleClose} centered>
        <Modal.Header closeButton>
          <Modal.Title>Edit Country</Modal.Title>
        </Modal.Header>
        <Modal.Body>
            <SelectWithInlineCreate
              label="Country *:"
              required
              value={country}
              onChange={setCountry}
              placeholderOption="Select Country"
              onAddClick={() => setAddCountryOpen(true)}
              overviewLinkEntity="country"
            >
              {countries.map((cntry) => (
                <option key={cntry.country_id} value={cntry.country_id} title={cntry.country_id}>
                  {cntry.title}
                </option>
              ))}
            </SelectWithInlineCreate>
        </Modal.Body>
        <Modal.Footer>
          <small className="mr-auto"><div style={{ color: country.trim() ? "green" : "red" }}>{country.trim() ? "Looks good." : "Country is required."}</div></small>
          <Button color="red" onClick={handleClose} disabled={isLoading}>Cancel</Button>
          <Button color="green" onClick={handleSave} disabled={!country.trim() || isLoading}>{isLoading ? "Saving..." : "Save Changes"}</Button>
        </Modal.Footer>
      </GuardedModal>

      <AddCountryModal
        nested
        hideTrigger
        show={addCountryOpen}
        onHide={() => setAddCountryOpen(false)}
        onCountryCreated={async (data) => {
          await loadCountries();
          const id = data?.country_id;
          if (id) setCountry(id);
          setAddCountryOpen(false);
        }}
      />
    </>
  );
}

// Edit Property Province Modal
export function EditPropertyProvinceModal({ property, update_state }) {
  const [show, setShow] = useState(false);
  const [province, setProvince] = useState("");
  const [provinces, setProvinces] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [addProvinceOpen, setAddProvinceOpen] = useState(false);
  const propertyCountryId = property.country?.country_id || "";

  useEffect(() => {
    if (show) {
      setProvince(property.province?.province_id || "");
      loadProvinces();
    }
  }, [show, property]);

  const loadProvinces = async () => {
    try {
      const currentHeaders = {
        ...headers,
        "Authorization": "Token " + localStorage.getItem("userToken")
      };
              const response = await axios.get("https://ultima.icsgr.com/api/regions/all_provinces/", {
        headers: currentHeaders
      });
      const provincesData = response?.data?.all_provinces || [];
      setProvinces(provincesData);
    } catch (error) {
      console.error('Error loading provinces:', error);
      setProvinces([]);
    }
  };

  const handleClose = () => setShow(false);
  const handleShow = () => setShow(true);

  const handleSave = async () => {
    if (!province.trim()) {
      Swal.fire("Error", "Province is required", "error");
      return;
    }

    setIsLoading(true);
    try {
      const currentHeaders = {
        ...headers,
        "Authorization": "Token " + localStorage.getItem("userToken")
      };

      const response = await axios.put(
        UPDATE_PROPERTY + property.property_id + "/",
        { province_id: province },
        { headers: currentHeaders }
      );

      Swal.fire("Success", "Province updated successfully", "success");
      if (update_state) update_state(response.data);
      handleClose();
    } catch (e) {
      console.error('Error updating province:', e);
      const apiMsg = e?.response?.data?.detail || e?.response?.data || "Failed to update province";
      Swal.fire("Error", apiMsg, "error");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <Button size="tiny" basic onClick={handleShow}><FiEdit style={{ marginRight: 6 }} /> Edit Province</Button>

      <GuardedModal show={show} onHide={handleClose} centered>
        <Modal.Header closeButton>
          <Modal.Title>Edit Province</Modal.Title>
        </Modal.Header>
        <Modal.Body>
            <SelectWithInlineCreate
              label="Province *:"
              required
              value={province}
              onChange={setProvince}
              placeholderOption="Select Province"
              onAddClick={!propertyCountryId ? undefined : () => setAddProvinceOpen(true)}
              overviewLinkEntity="province"
            >
              {provinces.map((prov) => (
                <option key={prov.province_id} value={prov.province_id} title={prov.province_id}>
                  {prov.title}
                </option>
              ))}
            </SelectWithInlineCreate>
        </Modal.Body>
        <Modal.Footer>
          <small className="mr-auto"><div style={{ color: province.trim() ? "green" : "red" }}>{province.trim() ? "Looks good." : "Province is required."}</div></small>
          <Button color="red" onClick={handleClose} disabled={isLoading}>Cancel</Button>
          <Button color="green" onClick={handleSave} disabled={!province.trim() || isLoading}>{isLoading ? "Saving..." : "Save Changes"}</Button>
        </Modal.Footer>
      </GuardedModal>

      <AddProvinceModal
        nested
        hideTrigger
        show={addProvinceOpen}
        onHide={() => setAddProvinceOpen(false)}
        defaultCountryId={propertyCountryId}
        lockCountry
        onProvinceCreated={async (data) => {
          await loadProvinces();
          const id = data?.province_id;
          if (id) setProvince(id);
          setAddProvinceOpen(false);
        }}
      />
    </>
  );
}

// Edit Property City Modal
export function EditPropertyCityModal({ property, update_state }) {
  const [show, setShow] = useState(false);
  const [city, setCity] = useState("");
  const [cities, setCities] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [addCityOpen, setAddCityOpen] = useState(false);
  const propertyCountryId = property.country?.country_id || "";
  const propertyProvinceId = property.province?.province_id || "";

  useEffect(() => {
    if (show) {
      setCity(property.city?.city_id || "");
      loadCities();
    }
  }, [show, property]);

  const loadCities = async () => {
    try {
      const currentHeaders = {
        ...headers,
        "Authorization": "Token " + localStorage.getItem("userToken")
      };
              const response = await axios.get("https://ultima.icsgr.com/api/regions/all_cities/", {
        headers: currentHeaders
      });
      const citiesData = response?.data?.all_cities || [];
      setCities(citiesData);
    } catch (error) {
      console.error('Error loading cities:', error);
      setCities([]);
    }
  };

  const handleClose = () => setShow(false);
  const handleShow = () => setShow(true);

  const handleSave = async () => {
    if (!city.trim()) {
      Swal.fire("Error", "City is required", "error");
      return;
    }

    setIsLoading(true);
    try {
      const currentHeaders = {
        ...headers,
        "Authorization": "Token " + localStorage.getItem("userToken")
      };

      const response = await axios.put(
        UPDATE_PROPERTY + property.property_id + "/",
        { city_id: city },
        { headers: currentHeaders }
      );

      Swal.fire("Success", "City updated successfully", "success");
      if (update_state) update_state(response.data);
      handleClose();
    } catch (e) {
      console.error('Error updating city:', e);
      const apiMsg = e?.response?.data?.detail || e?.response?.data || "Failed to update city";
      Swal.fire("Error", apiMsg, "error");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <Button size="tiny" basic onClick={handleShow}><FiEdit style={{ marginRight: 6 }} /> Edit City</Button>

      <GuardedModal show={show} onHide={handleClose} centered>
        <Modal.Header closeButton>
          <Modal.Title>Edit City</Modal.Title>
        </Modal.Header>
        <Modal.Body>
            <SelectWithInlineCreate
              label="City *:"
              required
              value={city}
              onChange={setCity}
              placeholderOption="Select City"
              onAddClick={!propertyProvinceId ? undefined : () => setAddCityOpen(true)}
              overviewLinkEntity="city"
            >
              {cities.map((cty) => (
                <option key={cty.city_id} value={cty.city_id} title={cty.city_id}>
                  {cityOptionLabel(cty)}
                </option>
              ))}
            </SelectWithInlineCreate>
        </Modal.Body>
        <Modal.Footer>
          <small className="mr-auto"><div style={{ color: city.trim() ? "green" : "red" }}>{city.trim() ? "Looks good." : "City is required."}</div></small>
          <Button color="red" onClick={handleClose} disabled={isLoading}>Cancel</Button>
          <Button color="green" onClick={handleSave} disabled={!city.trim() || isLoading}>{isLoading ? "Saving..." : "Save Changes"}</Button>
        </Modal.Footer>
      </GuardedModal>

      <AddCityModal
        nested
        hideTrigger
        show={addCityOpen}
        onHide={() => setAddCityOpen(false)}
        defaultCountryId={propertyCountryId}
        defaultProvinceId={propertyProvinceId}
        lockCountry
        lockProvince
        onCityCreated={async (data) => {
          await loadCities();
          const id = data?.city_id;
          if (id) setCity(id);
          setAddCityOpen(false);
        }}
      />
    </>
  );
}

// Edit Property Geo Location (Country + Province + City) Modal
export function EditPropertyGeoLocationModal({ property, update_state }) {
  const [show, setShow] = useState(false);
  const [country, setCountry] = useState("");
  const [province, setProvince] = useState("");
  const [city, setCity] = useState("");
  const [countries, setCountries] = useState([]);
  const [provinces, setProvinces] = useState([]);
  const [cities, setCities] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [addCountryOpen, setAddCountryOpen] = useState(false);
  const [addProvinceOpen, setAddProvinceOpen] = useState(false);
  const [addCityOpen, setAddCityOpen] = useState(false);

  const handleClose = () => setShow(false);
  const handleShow = () => setShow(true);

  useEffect(() => {
    if (show) {
      // Prefill from current property
      const currentCountry = property.country?.country_id || property.country?.country_id || property.country?.id || "";
      const currentProvince = property.province?.province_id || property.province?.id || "";
      const currentCity = property.city?.city_id || property.city?.id || "";
      setCountry(currentCountry);
      setProvince(currentProvince);
      setCity(currentCity);
      loadCountries().then(() => {
        if (currentCountry) {
          loadProvinces(currentCountry).then(() => {
            if (currentProvince) {
              loadCities(currentProvince);
            }
          });
        }
      });
    } else {
      // reset when closing
      setCountries([]);
      setProvinces([]);
      setCities([]);
      setCountry("");
      setProvince("");
      setCity("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show]);

  const loadCountries = async () => {
    try {
      const currentHeaders = {
        ...headers,
        "Authorization": "Token " + localStorage.getItem("userToken")
      };
      const response = await axios.get("https://ultima.icsgr.com/api/regions/all_countries/", { headers: currentHeaders });
      const countriesData = response?.data?.all_countries || [];
      setCountries(countriesData);
    } catch (error) {
      console.error('Error loading countries:', error);
      setCountries([]);
    }
  };

  const loadProvinces = async (countryId) => {
    try {
      const currentHeaders = {
        ...headers,
        "Authorization": "Token " + localStorage.getItem("userToken")
      };
      const response = await axios.get(`https://ultima.icsgr.com/api/regions/all_provinces/?country=${countryId}`, { headers: currentHeaders });
      const provincesData = response?.data?.all_provinces || [];
      setProvinces(provincesData);
    } catch (error) {
      console.error('Error loading provinces:', error);
      setProvinces([]);
    }
  };

  const loadCities = async (provinceId) => {
    try {
      const currentHeaders = {
        ...headers,
        "Authorization": "Token " + localStorage.getItem("userToken")
      };
      const response = await axios.get(`https://ultima.icsgr.com/api/regions/all_cities/?province=${provinceId}`, { headers: currentHeaders });
      const citiesData = response?.data?.all_cities || [];
      setCities(citiesData);
    } catch (error) {
      console.error('Error loading cities:', error);
      setCities([]);
    }
  };

  // Load options when parent location changes (do not wipe selected values here —
  // resets happen in onChange / create callbacks so initial prefill stays intact).
  useEffect(() => {
    if (!show) return;
    if (country) {
      loadProvinces(country);
    } else {
      setProvinces([]);
      setCities([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [country]);

  useEffect(() => {
    if (!show) return;
    if (province) {
      loadCities(province);
    } else {
      setCities([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [province]);

  const isValid = Boolean(country && province && city);

  const handleSave = async () => {
    if (!isValid) return;
    setIsLoading(true);
    try {
      const currentHeaders = {
        ...headers,
        "Authorization": "Token " + localStorage.getItem("userToken")
      };

      const response = await axios.put(
        UPDATE_PROPERTY + property.property_id + "/",
        { country_id: country, province_id: province, city_id: city },
        { headers: currentHeaders }
      );

      Swal.fire("Success", "Location updated successfully", "success");
      if (update_state) update_state(response.data);
      handleClose();
    } catch (e) {
      console.error('Error updating location (C/P/C):', e);
      const apiMsg = e?.response?.data?.detail || e?.response?.data || "Failed to update location";
      Swal.fire("Error", apiMsg, "error");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <Button size="tiny" basic onClick={handleShow}><FiEdit style={{ marginRight: 6 }} /> Edit Location</Button>

      <GuardedModal show={show} onHide={handleClose} centered>
        <Modal.Header closeButton>
          <Modal.Title>Edit Location</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <SelectWithInlineCreate
              label="Country *:"
              required
              className="mb-3"
              value={country}
              onChange={(v) => {
                setCountry(v);
                setProvince("");
                setCity("");
              }}
              placeholderOption="Select Country"
              onAddClick={() => setAddCountryOpen(true)}
              overviewLinkEntity="country"
            >
              {countries.map((cntry) => (
                <option key={cntry.country_id} value={cntry.country_id} title={cntry.country_id}>
                  {cntry.name || cntry.title}
                </option>
              ))}
            </SelectWithInlineCreate>
            <SelectWithInlineCreate
              label="Province *:"
              required
              className="mb-3"
              value={province}
              onChange={(v) => {
                setProvince(v);
                setCity("");
              }}
              disabled={!country}
              placeholderOption="Select Province"
              onAddClick={!country ? undefined : () => setAddProvinceOpen(true)}
              overviewLinkEntity="province"
            >
              {provinces.map((prov) => (
                <option key={prov.province_id} value={prov.province_id} title={prov.province_id}>
                  {prov.name || prov.title}
                </option>
              ))}
            </SelectWithInlineCreate>
            <SelectWithInlineCreate
              label="City *:"
              required
              className="mb-3"
              value={city}
              onChange={setCity}
              disabled={!province}
              placeholderOption="Select City"
              onAddClick={!province ? undefined : () => setAddCityOpen(true)}
              overviewLinkEntity="city"
            >
              {cities.map((cty) => (
                <option key={cty.city_id} value={cty.city_id} title={cty.city_id}>
                  {cty.name || cty.title}
                </option>
              ))}
            </SelectWithInlineCreate>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <small className="mr-auto"><div style={{ color: isValid ? "green" : "red" }}>{isValid ? "Looks good." : "Country, Province and City are required."}</div></small>
          <Button color="red" onClick={handleClose} disabled={isLoading}>Cancel</Button>
          <Button color="green" onClick={handleSave} disabled={!isValid || isLoading}>{isLoading ? "Saving..." : "Save Changes"}</Button>
        </Modal.Footer>
      </GuardedModal>

      <AddCountryModal
        nested
        hideTrigger
        show={addCountryOpen}
        onHide={() => setAddCountryOpen(false)}
        onCountryCreated={async (data) => {
          await loadCountries();
          const id = data?.country_id;
          if (id) {
            setCountry(id);
            setProvince("");
            setCity("");
          }
          setAddCountryOpen(false);
        }}
      />
      <AddProvinceModal
        nested
        hideTrigger
        show={addProvinceOpen}
        onHide={() => setAddProvinceOpen(false)}
        defaultCountryId={country}
        lockCountry
        onProvinceCreated={async (data) => {
          if (country) await loadProvinces(country);
          const id = data?.province_id;
          if (id) {
            setProvince(id);
            setCity("");
          }
          setAddProvinceOpen(false);
        }}
      />
      <AddCityModal
        nested
        hideTrigger
        show={addCityOpen}
        onHide={() => setAddCityOpen(false)}
        defaultCountryId={country}
        defaultProvinceId={province}
        lockCountry
        lockProvince
        onCityCreated={async (data) => {
          if (province) await loadCities(province);
          const id = data?.city_id;
          if (id) setCity(id);
          setAddCityOpen(false);
        }}
      />
    </>
  );
}

// Edit Property Location Modal
export function EditPropertyLocationModal({ property, update_state }) {
  const [show, setShow] = useState(false);
  const [location, setLocation] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (show) {
      setLocation(property.location || "");
    }
  }, [show, property]);

  const handleClose = () => setShow(false);
  const handleShow = () => setShow(true);

  const handleSave = async () => {
    if (!location.trim()) {
      Swal.fire("Error", "Location is required", "error");
      return;
    }

    setIsLoading(true);
    try {
      const currentHeaders = {
        ...headers,
        "Authorization": "Token " + localStorage.getItem("userToken")
      };

      const response = await axios.put(
        UPDATE_PROPERTY + property.property_id + "/",
        { location: location.trim() },
        { headers: currentHeaders }
      );

      Swal.fire("Success", "Location updated successfully", "success");
      if (update_state) update_state(response.data);
      handleClose();
    } catch (e) {
      console.error('Error updating location:', e);
      const apiMsg = e?.response?.data?.detail || e?.response?.data || "Failed to update location";
      Swal.fire("Error", apiMsg, "error");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <Button size="tiny" basic onClick={handleShow}><FiEdit style={{ marginRight: 6 }} /> Edit Location</Button>

      <GuardedModal show={show} onHide={handleClose} centered>
        <Modal.Header closeButton>
          <Modal.Title>Edit Location</Modal.Title>
        </Modal.Header>
        <Modal.Body>
            <Form.Group>
              <Form.Label>Location *:</Form.Label>
              <Form.Control
                type="text"
                value={location}
                onChange={(e) => setLocation(e.target.value)}
                placeholder="Enter location"
                maxLength={80}
              />
            </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <small className="mr-auto"><div style={{ color: location.trim() ? "green" : "red" }}>{location.trim() ? "Looks good." : "Location is required."}</div></small>
          <Button color="red" onClick={handleClose} disabled={isLoading}>Cancel</Button>
          <Button color="green" onClick={handleSave} disabled={!location.trim() || isLoading}>{isLoading ? "Saving..." : "Save Changes"}</Button>
        </Modal.Footer>
      </GuardedModal>
    </>
  );
}

// Edit Property Squared Meters Modal
export function EditPropertySquaredMetersModal({ property, update_state }) {
  const [show, setShow] = useState(false);
  const [squaredMeters, setSquaredMeters] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (show) {
      setSquaredMeters(formatDecimalWithComma(property.squared_meters));
    }
  }, [show, property]);

  const handleClose = () => setShow(false);
  const handleShow = () => setShow(true);

  const isValid = () => isValidDecimalInput(squaredMeters);

  const handleSave = async () => {
    const parsed = parseDecimalInput(squaredMeters);
    if (!isValid() || Number.isNaN(parsed)) {
      Swal.fire("Error", "Squared meters must be a valid number (0 or greater)", "error");
      return;
    }

    setIsLoading(true);
    try {
      const currentHeaders = {
        ...headers,
        "Authorization": "Token " + localStorage.getItem("userToken")
      };

      const response = await axios.put(
        UPDATE_PROPERTY + property.property_id + "/",
        { squared_meters: parsed },
        { headers: currentHeaders }
      );

      Swal.fire("Success", "Squared meters updated successfully", "success");
      if (update_state) update_state(response.data);
      handleClose();
    } catch (e) {
      console.error('Error updating squared meters:', e);
      const apiMsg = e?.response?.data?.detail || e?.response?.data || "Failed to update squared meters";
      Swal.fire("Error", apiMsg, "error");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <Button size="tiny" basic onClick={handleShow}><FiEdit style={{ marginRight: 6 }} /> Edit Squared meters</Button>

      <GuardedModal show={show} onHide={handleClose} centered>
        <Modal.Header closeButton>
          <Modal.Title>Edit Squared meters</Modal.Title>
        </Modal.Header>
        <Modal.Body>
            <Form.Group>
              <Form.Label>Squared meters:</Form.Label>
              <Form.Control
                type="text"
                inputMode="decimal"
                value={squaredMeters}
                onChange={(e) => setSquaredMeters(sanitizeDecimalInput(e.target.value))}
                placeholder="e.g. 85,5 or 85.5"
              />
            </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <small className="mr-auto"><div style={{ color: isValid() ? "green" : "red" }}>{isValid() ? "Looks good." : "Enter a valid number (0 or greater)."}</div></small>
          <Button color="red" onClick={handleClose} disabled={isLoading}>Cancel</Button>
          <Button color="green" onClick={handleSave} disabled={!isValid() || isLoading}>{isLoading ? "Saving..." : "Save Changes"}</Button>
        </Modal.Footer>
      </GuardedModal>
    </>
  );
}

// Edit Property Type Modal
export function EditPropertyTypeModal({ property, update_state }) {
  const [show, setShow] = useState(false);
  const [type, setType] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const typeChoices = [
    { value: "Plot", label: "Plot" },
    { value: "Land", label: "Land" },
    { value: "House", label: "House" },
    { value: "Apartment", label: "Apartment" },
    { value: "Store", label: "Store" },
    { value: "Other", label: "Other" },
  ];

  useEffect(() => {
    if (show) {
      setType(property.type || "");
    }
  }, [show, property]);

  const handleClose = () => setShow(false);
  const handleShow = () => setShow(true);

  const handleSave = async () => {
    if (!type.trim()) {
      Swal.fire("Error", "Type is required", "error");
      return;
    }

    setIsLoading(true);
    try {
      const currentHeaders = {
        ...headers,
        "Authorization": "Token " + localStorage.getItem("userToken")
      };

      const response = await axios.put(
        UPDATE_PROPERTY + property.property_id + "/",
        { type: type },
        { headers: currentHeaders }
      );

      Swal.fire("Success", "Type updated successfully", "success");
      if (update_state) update_state(response.data);
      handleClose();
    } catch (e) {
      console.error('Error updating type:', e);
      const apiMsg = e?.response?.data?.detail || e?.response?.data || "Failed to update type";
      Swal.fire("Error", apiMsg, "error");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <Button size="tiny" basic onClick={handleShow}><FiEdit style={{ marginRight: 6 }} /> Edit Type</Button>

      <GuardedModal show={show} onHide={handleClose} centered>
        <Modal.Header closeButton>
          <Modal.Title>Edit Type</Modal.Title>
        </Modal.Header>
        <Modal.Body>
            <Form.Group>
              <Form.Label>Type *:</Form.Label>
              <AutocompleteSelect value={type} onChange={setType} placeholder="Select Type">
                {typeChoices.map((choice) => (
                  <option key={choice.value} value={choice.value}>
                    {choice.label}
                  </option>
                ))}
              </AutocompleteSelect>
            </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <small className="mr-auto"><div style={{ color: type.trim() ? "green" : "red" }}>{type.trim() ? "Looks good." : "Type is required."}</div></small>
          <Button color="red" onClick={handleClose} disabled={isLoading}>Cancel</Button>
          <Button color="green" onClick={handleSave} disabled={!type.trim() || isLoading}>{isLoading ? "Saving..." : "Save Changes"}</Button>
        </Modal.Footer>
      </GuardedModal>
    </>
  );
}

// Edit Property Construction Year Modal
export function EditPropertyConstructYearModal({ property, update_state }) {
  const [show, setShow] = useState(false);
  const [constructyear, setConstructYear] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (show) {
      setConstructYear(property.constructyear || "");
    }
  }, [show, property]);

  const handleClose = () => setShow(false);
  const handleShow = () => setShow(true);

  const isValidYear = () => {
    if (String(constructyear).trim() === "") return true; // optional
    const y = parseInt(constructyear, 10);
    return Number.isInteger(y) && y >= 1800 && y <= 2100;
  };

  const handleSave = async () => {
    setIsLoading(true);
    try {
      const currentHeaders = {
        ...headers,
        "Authorization": "Token " + localStorage.getItem("userToken")
      };

      const response = await axios.put(
        UPDATE_PROPERTY + property.property_id + "/",
        { constructyear: (String(constructyear).trim() === "" ? null : String(constructyear).trim()) },
        { headers: currentHeaders }
      );

      Swal.fire("Success", "Construction year updated successfully", "success");
      if (update_state) update_state(response.data);
      handleClose();
    } catch (e) {
      console.error('Error updating construction year:', e);
      const apiMsg = e?.response?.data?.detail || e?.response?.data || "Failed to update construction year";
      Swal.fire("Error", apiMsg, "error");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <Button size="tiny" basic onClick={handleShow}><FiEdit style={{ marginRight: 6 }} /> Edit Construction Year</Button>

      <GuardedModal show={show} onHide={handleClose} centered>
        <Modal.Header closeButton>
          <Modal.Title>Edit Construction Year</Modal.Title>
        </Modal.Header>
        <Modal.Body>
            <Form.Group>
              <Form.Label>Construction Year:</Form.Label>
              <Form.Control
                type="number"
                value={constructyear}
                onChange={(e) => setConstructYear(e.target.value)}
                placeholder="Enter year (1800 - 2100)"
                min={1800}
                max={2100}
                step={1}
              />
            </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <small className="mr-auto"><div style={{ color: isValidYear() ? "green" : "red" }}>{isValidYear() ? "Looks good." : "Please enter a valid year."}</div></small>
          <Button color="red" onClick={handleClose} disabled={isLoading}>Cancel</Button>
          <Button color="green" onClick={handleSave} disabled={!isValidYear() || isLoading}>{isLoading ? "Saving..." : "Save Changes"}</Button>
        </Modal.Footer>
      </GuardedModal>
    </>
  );
}

// Edit Property Status Modal
export function EditPropertyStatusModal({ property, update_state }) {
  const [show, setShow] = useState(false);
  const [status, setStatus] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const statusChoices = [
    { value: "", label: "No Status" },
    { value: "Empty", label: "Empty" },
    { value: "Rented", label: "Rented" },
    { value: "Unfinished", label: "Unfinished" },
    { value: "Under offer", label: "Under offer" },
    { value: "Under contract", label: "Under contract" },
    { value: "Renovation", label: "Renovation" },
  ];

  useEffect(() => {
    if (show) {
      setStatus(property.status || "");
    }
  }, [show, property]);

  const handleClose = () => setShow(false);
  const handleShow = () => setShow(true);

  const handleSave = async () => {
    setIsLoading(true);
    try {
      const currentHeaders = {
        ...headers,
        "Authorization": "Token " + localStorage.getItem("userToken")
      };

      const response = await axios.put(
        UPDATE_PROPERTY + property.property_id + "/",
        { status: status || null },
        { headers: currentHeaders }
      );

      Swal.fire("Success", "Status updated successfully", "success");
      if (update_state) update_state(response.data);
      handleClose();
    } catch (e) {
      console.error('Error updating status:', e);
      const apiMsg = e?.response?.data?.detail || e?.response?.data || "Failed to update status";
      Swal.fire("Error", apiMsg, "error");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <Button size="tiny" basic onClick={handleShow}><FiEdit style={{ marginRight: 6 }} /> Edit Status</Button>

      <GuardedModal show={show} onHide={handleClose} centered>
        <Modal.Header closeButton>
          <Modal.Title>Edit Status</Modal.Title>
        </Modal.Header>
        <Modal.Body>
            <Form.Group>
              <Form.Label>Status:</Form.Label>
              <AutocompleteSelect value={status} onChange={setStatus}>
                {statusChoices.map((choice) => (
                  <option key={choice.value} value={choice.value}>
                    {choice.label}
                  </option>
                ))}
              </AutocompleteSelect>
            </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <small className="mr-auto"><div style={{ color: "green" }}>Looks good.</div></small>
          <Button color="red" onClick={handleClose} disabled={isLoading}>Cancel</Button>
          <Button color="green" onClick={handleSave} disabled={isLoading}>{isLoading ? "Saving..." : "Save Changes"}</Button>
        </Modal.Footer>
      </GuardedModal>
    </>
  );
}

// Edit Property Market Modal
export function EditPropertyMarketModal({ property, update_state }) {
  const [show, setShow] = useState(false);
  const [market, setMarket] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const marketChoices = [
    { value: "", label: "No Market" },
    { value: "ShortTerm", label: "Short Term" },
    { value: "LongTerm", label: "Long Term" },
    { value: "Sale", label: "Sale" },
    { value: "Wait", label: "Wait" },
    { value: "Own", label: "Own" },
  ];

  useEffect(() => {
    if (show) {
      setMarket(property.market || "");
    }
  }, [show, property]);

  const handleClose = () => setShow(false);
  const handleShow = () => setShow(true);

  const handleSave = async () => {
    setIsLoading(true);
    try {
      const currentHeaders = {
        ...headers,
        "Authorization": "Token " + localStorage.getItem("userToken")
      };

      const response = await axios.put(
        UPDATE_PROPERTY + property.property_id + "/",
        { market: market || null },
        { headers: currentHeaders }
      );

      Swal.fire("Success", "Market updated successfully", "success");
      if (update_state) update_state(response.data);
      handleClose();
    } catch (e) {
      console.error('Error updating market:', e);
      const apiMsg = e?.response?.data?.detail || e?.response?.data || "Failed to update market";
      Swal.fire("Error", apiMsg, "error");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <Button size="tiny" basic onClick={handleShow}><FiEdit style={{ marginRight: 6 }} /> Edit Market</Button>

      <GuardedModal show={show} onHide={handleClose} centered>
        <Modal.Header closeButton>
          <Modal.Title>Edit Market</Modal.Title>
        </Modal.Header>
        <Modal.Body>
            <Form.Group>
              <Form.Label>Market:</Form.Label>
              <AutocompleteSelect value={market} onChange={setMarket}>
                {marketChoices.map((choice) => (
                  <option key={choice.value} value={choice.value}>
                    {choice.label}
                  </option>
                ))}
              </AutocompleteSelect>
            </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <small className="mr-auto"><div style={{ color: "green" }}>Looks good.</div></small>
          <Button color="red" onClick={handleClose} disabled={isLoading}>Cancel</Button>
          <Button color="green" onClick={handleSave} disabled={isLoading}>{isLoading ? "Saving..." : "Save Changes"}</Button>
        </Modal.Footer>
      </GuardedModal>
    </>
  );
}

// Edit Property Broker Modal
export function EditPropertyBrokerModal({ property, update_state }) {
  const [show, setShow] = useState(false);
  const [broker, setBroker] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (show) {
      setBroker(property.broker || "");
    }
  }, [show, property]);

  const handleClose = () => setShow(false);
  const handleShow = () => setShow(true);

  const handleSave = async () => {
    setIsLoading(true);
    try {
      const currentHeaders = {
        ...headers,
        "Authorization": "Token " + localStorage.getItem("userToken")
      };

      const response = await axios.put(
        UPDATE_PROPERTY + property.property_id + "/",
        { broker: broker.trim() || null },
        { headers: currentHeaders }
      );

      Swal.fire("Success", "Broker updated successfully", "success");
      if (update_state) update_state(response.data);
      handleClose();
    } catch (e) {
      console.error('Error updating broker:', e);
      const apiMsg = e?.response?.data?.detail || e?.response?.data || "Failed to update broker";
      Swal.fire("Error", apiMsg, "error");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <Button size="tiny" basic onClick={handleShow}><FiEdit style={{ marginRight: 6 }} /> Edit Broker</Button>

      <GuardedModal show={show} onHide={handleClose} centered>
        <Modal.Header closeButton>
          <Modal.Title>Edit Broker</Modal.Title>
        </Modal.Header>
        <Modal.Body>
            <Form.Group>
              <Form.Label>Broker:</Form.Label>
              <Form.Control
                type="text"
                value={broker}
                onChange={(e) => setBroker(e.target.value)}
                placeholder="Enter broker name"
                maxLength={120}
              />
            </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <small className="mr-auto"><div style={{ color: "green" }}>Looks good.</div></small>
          <Button color="red" onClick={handleClose} disabled={isLoading}>Cancel</Button>
          <Button color="green" onClick={handleSave} disabled={isLoading}>{isLoading ? "Saving..." : "Save Changes"}</Button>
        </Modal.Footer>
      </GuardedModal>
    </>
  );
}

// Edit Property Active Modal
export function EditPropertyActiveModal({ property, update_state }) {
  const [show, setShow] = useState(false);
  const [active, setActive] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (show) {
      setActive(property.active || false);
    }
  }, [show, property]);

  const handleClose = () => setShow(false);
  const handleShow = () => setShow(true);

  const handleSave = async () => {
    setIsLoading(true);
    try {
      const currentHeaders = {
        ...headers,
        "Authorization": "Token " + localStorage.getItem("userToken")
      };

      const response = await axios.put(
        UPDATE_PROPERTY + property.property_id + "/",
        { active: active },
        { headers: currentHeaders }
      );

      Swal.fire("Success", "Active status updated successfully", "success");
      if (update_state) update_state(response.data);
      handleClose();
    } catch (e) {
      console.error('Error updating active status:', e);
      const apiMsg = e?.response?.data?.detail || e?.response?.data || "Failed to update active status";
      Swal.fire("Error", apiMsg, "error");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <Button size="tiny" basic onClick={handleShow}><FiEdit style={{ marginRight: 6 }} /> Edit Active</Button>

      <GuardedModal show={show} onHide={handleClose} centered>
        <Modal.Header closeButton>
          <Modal.Title>Edit Active Status</Modal.Title>
        </Modal.Header>
        <Modal.Body>
            <Form.Group>
              <Form.Check
                type="checkbox"
                label="Is Active Property"
                checked={active}
                onChange={(e) => setActive(e.target.checked)}
              />
            </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <small className="mr-auto"><div style={{ color: "green" }}>Looks good.</div></small>
          <Button color="red" onClick={handleClose} disabled={isLoading}>Cancel</Button>
          <Button color="green" onClick={handleSave} disabled={isLoading}>{isLoading ? "Saving..." : "Save Changes"}</Button>
        </Modal.Footer>
      </GuardedModal>
    </>
  );
}

// Edit Property Notes Modal
export function EditPropertyNotesModal({ property, update_state }) {
  const [show, setShow] = useState(false);
  const [notes, setNotes] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (show) {
      setNotes(property.notes || "");
    }
  }, [show, property]);

  const handleClose = () => setShow(false);
  const handleShow = () => setShow(true);

  const handleSave = async () => {
    setIsLoading(true);
    try {
      const currentHeaders = {
        ...headers,
        "Authorization": "Token " + localStorage.getItem("userToken")
      };

      const response = await axios.put(
        UPDATE_PROPERTY + property.property_id + "/",
        { notes: htmlForApi(notes) },
        { headers: currentHeaders }
      );

      Swal.fire("Success", "Notes updated successfully", "success");
      if (update_state) update_state(response.data);
      handleClose();
    } catch (e) {
      console.error('Error updating notes:', e);
      const apiMsg = e?.response?.data?.detail || e?.response?.data || "Failed to update notes";
      Swal.fire("Error", apiMsg, "error");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <Button size="tiny" basic onClick={handleShow}><FiEdit style={{ marginRight: 6 }} /> Edit Notes</Button>

      <GuardedModal show={show} onHide={handleClose} centered>
        <Modal.Header closeButton>
          <Modal.Title>Edit Notes</Modal.Title>
        </Modal.Header>
        <Modal.Body>
            <Form.Group>
              <Form.Label>Notes:</Form.Label>
              <RichTextEditor
                rows={3}
                value={notes}
                onChange={setNotes}
                placeholder="Enter notes"
              />
            </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <small className="mr-auto"><div style={{ color: "green" }}>Looks good.</div></small>
          <Button color="red" onClick={handleClose} disabled={isLoading}>Cancel</Button>
          <Button color="green" onClick={handleSave} disabled={isLoading}>{isLoading ? "Saving..." : "Save Changes"}</Button>
        </Modal.Footer>
      </GuardedModal>
    </>
  );
}

// Edit Property linked clients (from project's associated clients, multi-select)
export function EditPropertyRelatedClientsModal({ property, update_state }) {
  const [show, setShow] = useState(false);
  const [clientOptions, setClientOptions] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!show) return;
    const pid = property.project?.project_id;
    const existing = (property.related_clients || []).map((c) => c.client_id).filter(Boolean);
    setSelectedIds(existing);

    const load = async () => {
      if (!pid) {
        setClientOptions([]);
        return;
      }
      try {
        const currentHeaders = {
          ...headers,
          Authorization: "Token " + localStorage.getItem("userToken"),
        };
        const res = await axios.get(ASSOCIATED_CLIENTS, { headers: currentHeaders });
        const all = res.data?.all_associated_clients || [];
        const byId = {};
        all.forEach((ac) => {
          if (ac.project?.project_id !== pid) return;
          const c = ac.client;
          const cid = c?.client_id;
          if (c && cid && !byId[cid]) byId[cid] = c;
        });
        setClientOptions(Object.values(byId));
      } catch (e) {
        console.error("Error loading associated clients:", e);
        setClientOptions([]);
      }
    };
    load();
  }, [show, property]);

  const handleClose = () => setShow(false);
  const handleShow = () => setShow(true);

  const handleSave = async () => {
    setIsLoading(true);
    try {
      const currentHeaders = {
        ...headers,
        Authorization: "Token " + localStorage.getItem("userToken"),
      };
      const response = await axios.put(
        UPDATE_PROPERTY + property.property_id + "/",
        { related_client_ids: selectedIds.join(",") },
        { headers: currentHeaders }
      );
      Swal.fire("Success", "Linked clients updated successfully", "success");
      if (update_state) update_state(response.data);
      handleClose();
    } catch (e) {
      console.error("Error updating linked clients:", e);
      const data = e?.response?.data;
      let apiMsg = data?.detail || data?.error;
      if (!apiMsg && data && typeof data === "object") {
        const parts = Object.entries(data).map(([k, v]) => {
          const s = Array.isArray(v) ? v.join(" ") : String(v);
          return `${k}: ${s}`;
        });
        if (parts.length) apiMsg = parts.join("\n");
      }
      Swal.fire("Error", apiMsg || "Failed to update linked clients", "error");
    } finally {
      setIsLoading(false);
    }
  };

  const pid = property.project?.project_id;

  return (
    <>
      <Button size="tiny" basic onClick={handleShow}>
        <FiEdit style={{ marginRight: 6 }} /> Edit linked clients
      </Button>

      <GuardedModal show={show} onHide={handleClose} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Linked clients (from project associations)</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {!pid ? (
            <p className="text-muted">This property has no project; link a project first.</p>
          ) : clientOptions.length === 0 ? (
            <p className="text-muted">No associated clients on this project yet.</p>
          ) : (
            <div
              style={{
                maxHeight: 320,
                overflowY: "auto",
                border: "1px solid #dee2e6",
                borderRadius: 4,
                padding: "10px 12px",
              }}
            >
              {clientOptions.map((cli) => {
                const cid = cli.client_id;
                const label =
                  cli.fullname ||
                  `${cli.surname || ""} ${cli.name || ""}`.trim() ||
                  cid;
                return (
                  <Form.Check
                    key={cid}
                    type="checkbox"
                    id={`edit-prop-rc-${cid}`}
                    className="mb-2"
                    label={`${cid} — ${label}`}
                    checked={selectedIds.includes(cid)}
                    onChange={() => {
                      setSelectedIds((prev) =>
                        prev.includes(cid) ? prev.filter((x) => x !== cid) : [...prev, cid]
                      );
                    }}
                  />
                );
              })}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button color="red" onClick={handleClose} disabled={isLoading}>
            Cancel
          </Button>
          <Button color="green" onClick={handleSave} disabled={isLoading || !pid}>
            {isLoading ? "Saving..." : "Save Changes"}
          </Button>
        </Modal.Footer>
      </GuardedModal>
    </>
  );
}

function EditPropertyUtilityTextModal({
  property,
  update_state,
  field,
  label,
  successMessage,
  errorMessage,
}) {
  const [show, setShow] = useState(false);
  const [value, setValue] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (show) {
      setValue(property[field] || "");
    }
  }, [show, property, field]);

  const handleClose = () => setShow(false);
  const handleShow = () => setShow(true);

  const handleSave = async () => {
    setIsLoading(true);
    try {
      const currentHeaders = {
        ...headers,
        "Authorization": "Token " + localStorage.getItem("userToken")
      };

      const response = await axios.put(
        UPDATE_PROPERTY + property.property_id + "/",
        { [field]: value.trim() || null },
        { headers: currentHeaders }
      );

      Swal.fire("Success", successMessage, "success");
      if (update_state) update_state(response.data);
      handleClose();
    } catch (e) {
      console.error(`Error updating ${field}:`, e);
      const apiMsg = e?.response?.data?.detail || e?.response?.data || errorMessage;
      Swal.fire("Error", apiMsg, "error");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <Button size="tiny" basic onClick={handleShow}><FiEdit style={{ marginRight: 6 }} /> Edit {label}</Button>

      <GuardedModal show={show} onHide={handleClose} centered>
        <Modal.Header closeButton>
          <Modal.Title>Edit {label}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
            <Form.Group>
              <Form.Label>{label}:</Form.Label>
              <Form.Control
                type="text"
                value={value}
                onChange={(e) => setValue(e.target.value)}
                placeholder={`Enter ${label.toLowerCase()}`}
                maxLength={120}
              />
            </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <small className="mr-auto"><div style={{ color: "green" }}>Looks good.</div></small>
          <Button color="red" onClick={handleClose} disabled={isLoading}>Cancel</Button>
          <Button color="green" onClick={handleSave} disabled={isLoading}>{isLoading ? "Saving..." : "Save Changes"}</Button>
        </Modal.Footer>
      </GuardedModal>
    </>
  );
}

export function EditPropertyPowerModal({ property, update_state }) {
  return (
    <EditPropertyUtilityTextModal
      property={property}
      update_state={update_state}
      field="power"
      label="Power"
      successMessage="Power updated successfully"
      errorMessage="Failed to update power"
    />
  );
}

export function EditPropertyWaterModal({ property, update_state }) {
  return (
    <EditPropertyUtilityTextModal
      property={property}
      update_state={update_state}
      field="water"
      label="Water"
      successMessage="Water updated successfully"
      errorMessage="Failed to update water"
    />
  );
}

export function EditPropertyBuildingMaintenanceFeesModal({ property, update_state }) {
  return (
    <EditPropertyUtilityTextModal
      property={property}
      update_state={update_state}
      field="building_maintenance_fees"
      label="Building maintenance fees"
      successMessage="Building maintenance fees updated successfully"
      errorMessage="Failed to update building maintenance fees"
    />
  );
}

export function EditPropertyValueModal({ property, update_state }) {
  return (
    <EditPropertyUtilityTextModal
      property={property}
      update_state={update_state}
      field="property_value"
      label="Value of property"
      successMessage="Value of property updated successfully"
      errorMessage="Failed to update value of property"
    />
  );
}
